import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useUiStore } from '@/stores/uiStore';
import { performLogout } from '@/hooks/useAuth';

type NavItem = {
  to: string;
  label: string;
  icon: ReactNode;
};

const NAV_ITEMS: NavItem[] = [
  {
    to: '/dashboard',
    label: 'Tableau de Bord',
    icon: <Square />,
  },
  {
    to: '/planning',
    label: 'Planning',
    icon: <Calendar />,
  },
  {
    to: '/clients',
    label: 'Clients',
    icon: <Users />,
  },
  {
    to: '/attente-pieces',
    label: 'Attente pièces',
    icon: <Package />,
  },
  {
    to: '/parametres',
    label: 'Paramètres',
    icon: <Cog />,
  },
];

export default function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const openNewRdv = useUiStore((s) => s.openNewRdvModal);
  const navigate = useNavigate();

  async function handleLogout() {
    await performLogout();
    navigate('/login', { replace: true });
  }

  return (
    <aside className="w-[240px] shrink-0 bg-surface-white border-r border-border-light flex flex-col h-screen sticky top-0">
      <div className="px-5 pt-6 pb-4">
        <div className="text-lg font-bold text-ktm-orange tracking-tight leading-none">
          KTM MAROC
        </div>
        <div className="text-xs text-text-muted mt-1 uppercase tracking-wider">
          Casablanca Atelier
        </div>
      </div>

      <button
        type="button"
        data-testid="open-new-rdv"
        onClick={() => openNewRdv()}
        className="mx-5 mb-4 btn btn-primary justify-center"
      >
        + Nouveau RDV
      </button>

      <nav className="flex-1 px-2 space-y-0.5">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-ktm-orange-light text-ktm-orange'
                  : 'text-text-secondary hover:bg-surface-active'
              }`
            }
          >
            <span className="w-4 h-4 shrink-0">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}

        {/* Guide d'utilisation (fichier statique servi par le frontend, ouvre un nouvel onglet) */}
        <a
          href="/guide-utilisation.html"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-text-secondary hover:bg-surface-active transition-colors"
        >
          <span className="w-4 h-4 shrink-0"><Help /></span>
          Aide
        </a>
      </nav>

      {user && (
        <div className="border-t border-border-light px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-sm bg-ktm-orange text-text-on-orange flex items-center justify-center font-bold text-sm">
              {user.prenom.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-text-primary truncate">
                {user.prenom} {user.nom}
              </div>
              <div className="text-xs text-text-muted uppercase">{user.role}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="text-xs text-text-muted hover:text-status-annule-text"
          >
            Déconnexion
          </button>
        </div>
      )}
    </aside>
  );
}

function Square() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function Calendar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18" />
      <path d="M8 3v4M16 3v4" />
    </svg>
  );
}

function Users() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M14.5 20a4.5 4.5 0 0 1 7 0" />
    </svg>
  );
}

function Help() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function Package() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
      <path d="M16.5 9.4 7.55 4.24" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96 12 12.01l8.73-5.05" />
      <path d="M12 22.08V12" />
    </svg>
  );
}

function Cog() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15 1.7 1.7 0 0 0 3.04 14H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.56V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.56 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1z" />
    </svg>
  );
}
